import React, {
  FC,
  FormEvent,
  FormEventHandler,
  MouseEventHandler,
  useEffect,
  useMemo,
} from "react";
import {
  CTA,
  DownloadablePdfUrl,
  MutateTaskInputs,
  ProductPhase,
  TaskTemplateId,
} from "../types";
import { startCase } from "lodash";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import TextArea from "@common/components/TextArea";
import {
  estimateCompletionTimeOptions,
  phaseOptions,
  taskActionOptions,
  topicOptions,
  downloadablePdfUrlOptions,
  NOTIFICATION_DISABLED_REASONS,
} from "../constants";
import {
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  SelectBox,
  TextInput,
  InputLabel,
  Tooltip,
} from "@earned/core";
import { CgSpinner } from "react-icons/cg";
import moment from "moment";
import { useIsNotifyDisabled } from "../hooks.web";
import { getTaskDueDatePayload } from "../utils";

interface MutateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeleteTask: () => void;
  onConfirm: (onConfirmInput: {
    name: string;
    description?: string;
    taskId?: string;
    estimatedCompletionTime?: number;
    callToAction?: string;
    productTopic?: string;
    productPhase?: string;
    templateId: TaskTemplateId;
    downloadablePdfUrl?: string;
    triggerNotification?: boolean;
    ctaUrl?: string;
    ctaText?: string;
    dueDate?: string;
  }) => Promise<Forme.Task | void>;
  defaultPhase: ProductPhase;
  editParams?: Forme.Task | null;
  isEdit?: boolean;
  household: Forme.HouseholdWithClients | undefined;
}

const MutateTaskModal: FC<MutateTaskModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onDeleteTask,
  defaultPhase,
  editParams,
  isEdit = false,
  household,
}) => {
  const MutateTaskSchema = yup.object({
    productPhase: yup.string().required("Task phase is required"),
    productTopic: yup.string().nullable(),
    taskName: yup.string().required("Task name is required"),
    triggerNotification: yup.boolean().nullable(),
    estimateCompletionTime: yup.number().nullable(),
    description: yup.string(),
    callToAction: yup.string().nullable(),
    ctaUrl: yup.string().when("callToAction", {
      is: (value: string) =>
        value === CTA.OPEN_URL || value === CTA.SCHEDULE_MEETING_ADVISOR,
      then: yup
        .string()
        .url("Please enter a valid URL")
        .required("URL is required"),
      otherwise: yup.string().nullable(),
    }),
    ctaText: yup.string().when("callToAction", {
      is: CTA.OPEN_URL,
      then: yup.string().required("CTA text is required"),
      otherwise: yup.string().nullable(),
    }),
    downloadablePdfUrl: yup.string().when("callToAction", {
      is: CTA.DOWNLOAD_PDF,
      then: yup.string().required("Downloadable PDF is required.").nullable(),
      otherwise: yup.string().nullable(),
    }),
    dueDate: yup.date().nullable(),
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    trigger,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useForm<MutateTaskInputs>({
    resolver: yupResolver(MutateTaskSchema),
    mode: "onTouched",
    reValidateMode: "onBlur",
    criteriaMode: "firstError",
  });

  const isNotifyDisabledFn = useIsNotifyDisabled(household);
  const { disabled: isNotifyDisabled } = isNotifyDisabledFn();

  const onSubmit:
    | MouseEventHandler<HTMLButtonElement>
    | FormEventHandler<HTMLFormElement> = (event: FormEvent | MouseEvent) => {
    event.preventDefault();
    handleSubmit(async (data) => {
      await trigger("productTopic");
      await trigger("downloadablePdfUrl");
      await onConfirm({
        taskId: editParams?.id,
        name: data.taskName,
        description: data.description || "",
        productPhase: data.productPhase,
        productTopic: data.productTopic,
        estimatedCompletionTime: data.estimatedCompletionTime as number,
        callToAction: data.callToAction,
        downloadablePdfUrl: data.downloadablePdfUrl,
        triggerNotification: data.triggerNotification,
        ctaUrl: data.ctaUrl,
        ctaText: data.ctaText,
        dueDate: data.dueDate ? getTaskDueDatePayload(data.dueDate) : undefined,
        templateId: editParams?.templateId as TaskTemplateId,
      });
      onClose();
    })(event as FormEvent);
  };

  useEffect(() => {
    if (editParams) {
      setValue("taskName", editParams.name);
      setValue("description", editParams.description);
      setValue("productPhase", editParams.productPhase);
      setValue(
        "estimatedCompletionTime",
        editParams.estimatedCompletionTime as number
      );
      setValue("callToAction", editParams.callToAction as CTA);
      setValue("productTopic", editParams.productTopic);
      setValue(
        "downloadablePdfUrl",
        editParams.downloadablePdfUrl as DownloadablePdfUrl
      );
      setValue("ctaUrl", editParams.ctaUrl || "");
      setValue("ctaText", editParams.ctaText || "");
      setValue("dueDate", editParams.dueDate ? editParams.dueDate : null);
    } else {
      setValue("taskName", "");
      setValue("description", "");
      setValue("productPhase", defaultPhase);
      setValue("estimatedCompletionTime", 0);
      setValue("downloadablePdfUrl", undefined);
      setValue("callToAction", undefined);
      setValue("productTopic", undefined);
      setValue("triggerNotification", !isNotifyDisabled);
      setValue("ctaUrl", "");
      setValue("ctaText", "");
      setValue("dueDate", null);
    }
    // Clear all validation errors when modal opens or data changes
    clearErrors();
  }, [
    isEdit,
    editParams,
    setValue,
    isOpen,
    defaultPhase,
    isNotifyDisabled,
    clearErrors,
  ]);

  const [productPhase, callToAction] = watch(["productPhase", "callToAction"]);

  const selectPhaseOptions = useMemo(() => {
    return phaseOptions.map((option) => ({
      value: option.id,
      label: option.name,
      id: option.id,
    }));
  }, []);

  const selectedPhase = useMemo(() => {
    return (
      selectPhaseOptions?.find((opt) => opt.id === productPhase) ||
      selectPhaseOptions[0]
    );
  }, [productPhase, selectPhaseOptions]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      classNames={{}}
      title={isEdit ? "Edit Task" : "Create New Task"}
    >
      <form onSubmit={onSubmit as FormEventHandler}>
        <div>
          <Controller
            control={control}
            name="productPhase"
            render={({ field }) => (
              <SelectBox
                options={selectPhaseOptions}
                selected={selectedPhase}
                onChange={(option) => {
                  field.onChange(option.id);
                }}
                label="Assign a phase"
                required
                error={errors.productPhase?.message}
              />
            )}
          />

          {productPhase === ProductPhase.PHASE_2 && (
            <Controller
              control={control}
              name="productTopic"
              render={({ field }) => (
                <SelectBox
                  options={topicOptions}
                  selected={
                    topicOptions?.find((opt) => opt.id === field.value) ||
                    topicOptions[0]
                  }
                  onChange={(option) => {
                    field.onChange(option.id);
                  }}
                  error={errors?.productTopic?.message}
                  label="Playbook Category"
                  required
                />
              )}
            />
          )}
          <TextInput
            required
            label="Task Name"
            placeholder="Add task name"
            {...register("taskName")}
            error={errors.taskName?.message}
          />

          <div className="pb-6">
            <TextArea
              rows={4}
              label="Description"
              className="text-label-md mb-px w-full resize-none rounded border border-gray-300 px-[9px] py-3 text-gray-700 outline-green-100 focus:border-green-100 focus:outline-none focus:ring-0"
              placeholder="Describe task"
              {...register("description")}
              error={errors?.description}
            />
          </div>

          <Controller
            name="estimatedCompletionTime"
            control={control}
            render={({ field }) => (
              <SelectBox
                options={estimateCompletionTimeOptions}
                selected={
                  estimateCompletionTimeOptions?.find(
                    (opt) => opt.id === field.value
                  ) || estimateCompletionTimeOptions[0]
                }
                onChange={(option) => {
                  field.onChange(option.id);
                }}
                label="Completion time"
                showOptional
              />
            )}
          />

          <div className="pb-6">
            <Controller
              name="dueDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  date={field.value ? moment(field.value) : null}
                  onDateChange={(date) =>
                    field.onChange(date ? date.toDate() : null)
                  }
                  label="Due Date"
                  showOptional
                />
              )}
            />
          </div>

          <Controller
            name="callToAction"
            control={control}
            render={({ field }) => (
              <SelectBox
                selected={{
                  id: field.value as CTA,
                  label: startCase(field?.value ?? "None"),
                  value: field.value as CTA,
                }}
                options={taskActionOptions}
                onChange={(option) => {
                  field.onChange(option.id);
                  if (
                    option.id !== CTA.OPEN_URL &&
                    option.id !== CTA.SCHEDULE_MEETING_ADVISOR
                  ) {
                    setValue("ctaUrl", "");
                  }
                }}
                error={errors?.callToAction?.message}
                label="Task Action Button"
                showOptional
              />
            )}
          />

          {callToAction === CTA.OPEN_URL && (
            <>
              <TextInput
                required
                label="URL"
                placeholder="Enter URL"
                {...register("ctaUrl")}
                error={errors.ctaUrl?.message}
              />
              <TextInput
                required
                label="Call To Action Text"
                placeholder="Enter Call To Action text"
                {...register("ctaText")}
                error={errors.ctaText?.message}
              />
            </>
          )}
          {callToAction === CTA.SCHEDULE_MEETING_ADVISOR && (
            <>
              <TextInput
                required
                label="Calendly URL"
                placeholder="Enter URL"
                {...register("ctaUrl")}
                error={errors.ctaUrl?.message}
              />
            </>
          )}
          {callToAction === CTA.DOWNLOAD_PDF && (
            <Controller
              control={control}
              name="downloadablePdfUrl"
              render={({ field }) => (
                <SelectBox
                  options={downloadablePdfUrlOptions}
                  selected={{
                    id: field.value as DownloadablePdfUrl,
                    label: startCase(
                      (Object.keys(DownloadablePdfUrl)
                        .find(
                          (key) =>
                            DownloadablePdfUrl[
                              key as keyof typeof DownloadablePdfUrl
                            ] === field.value
                        )
                        ?.toLowerCase() as string) ?? "None"
                    ),
                    value: field.value as DownloadablePdfUrl,
                  }}
                  onChange={(option) => {
                    field.onChange(option.id);
                    trigger("downloadablePdfUrl");
                  }}
                  error={errors?.downloadablePdfUrl?.message}
                  label="Downloadable PDF"
                  required
                />
              )}
            />
          )}
          {!isEdit && (
            <div className="col-span-1">
              {isNotifyDisabled ? (
                <Tooltip
                  title="Notifications are disabled"
                  description={
                    NOTIFICATION_DISABLED_REASONS.NO_TASK_NOTIFICATIONS_ENABLED
                  }
                >
                  <InputLabel label="Notify Client" showOptional />
                  <Checkbox
                    checked={false}
                    label="Send Notification"
                    onChange={() => undefined}
                    disabled
                  />
                </Tooltip>
              ) : (
                <>
                  <InputLabel label="Notify Client" showOptional />
                  <div className="mt-1 flex flex-row items-center gap-x-1">
                    <Controller
                      name="triggerNotification"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          checked={field.value}
                          label="Send Notification"
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                          disabled={isNotifyDisabled}
                        />
                      )}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <div className="flex flex-row items-center justify-between pt-10">
            <div>
              {isEdit && (
                <Button
                  variant="danger"
                  onClick={(event) => {
                    // Prevents onSubmit from firing.
                    event.preventDefault();
                    onDeleteTask();
                  }}
                >
                  Delete task
                </Button>
              )}
            </div>
            <div className="flex flex-row gap-x-2">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={onSubmit as FormEventHandler}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <CgSpinner
                    size={16}
                    color={"dark-gray"}
                    className="animate-spin"
                  />
                ) : (
                  <>{isEdit ? "Save task" : "Create task"}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Dialog>
  );
};

export default MutateTaskModal;
